import { SettingsHelp, SettingsSaveBar } from "./ProposalSettingsUI";
/**
 * TierNamesTab - Rename the three pricing tiers for the whole organization.
 *
 * Proposals always store tiers under the fixed slugs good/better/best; this is
 * where an org decides that "Good" is actually called "Essential". The names
 * apply everywhere a tier is shown: the proposal builder, the customer-facing
 * proposal page, and generated PDFs.
 */

import { useEffect, useMemo, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import toast from "react-hot-toast";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  FormControl,
  FormHelperText,
  FormLabel,
  Input,
  Stack,
  Typography,
} from "@mui/joy";
import {
  ArrowCounterClockwise as Reset,
  Info,
  Tag,
} from "@phosphor-icons/react";
import { proposalsApi } from "../../lib/proposalsApi";
import {
  TIER_LABELS_QUERY_KEY,
  useTierLabels,
} from "../../hooks/useTierLabels";
import { DEFAULT_TIER_LABELS, TIER_TYPES } from "../../types/proposals";
import type { TierLabelMap, TierType } from "../../types/proposals";

const TIER_COLORS: Record<TierType, "warning" | "primary" | "success"> = {
  good: "warning",
  better: "primary",
  best: "success",
};

const MAX_LENGTH = 50;

export default function TierNamesTab({
  onDirtyChange,
}: {
  onDirtyChange?: (dirty: boolean) => void;
}) {
  const queryClient = useQueryClient();
  const { labels, isLoading } = useTierLabels();
  const [draft, setDraft] = useState<TierLabelMap>(DEFAULT_TIER_LABELS);

  // Re-seed the draft whenever the saved names change (initial load, reset)
  useEffect(() => {
    setDraft(labels);
  }, [labels]);

  const trimmed = useMemo(
    () =>
      TIER_TYPES.reduce((acc, tierType) => {
        acc[tierType] = draft[tierType].trim();
        return acc;
      }, {} as TierLabelMap),
    [draft],
  );

  const blankTier = TIER_TYPES.find((tierType) => !trimmed[tierType]);
  const duplicateTier = TIER_TYPES.find(
    (tierType, index) =>
      trimmed[tierType] &&
      TIER_TYPES.some(
        (other, otherIndex) =>
          otherIndex < index &&
          trimmed[other].toLowerCase() === trimmed[tierType].toLowerCase(),
      ),
  );
  const dirty = TIER_TYPES.some(
    (tierType) => trimmed[tierType] !== labels[tierType],
  );
  useEffect(() => {
    onDirtyChange?.(dirty);
    return () => onDirtyChange?.(false);
  }, [dirty, onDirtyChange]);
  const usingDefaults = TIER_TYPES.every(
    (tierType) => labels[tierType] === DEFAULT_TIER_LABELS[tierType],
  );

  const saveMutation = useMutation({
    mutationFn: () =>
      proposalsApi.updateTierLabels(
        TIER_TYPES.map((tierType) => ({
          tier_type: tierType,
          label: trimmed[tierType],
        })),
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: TIER_LABELS_QUERY_KEY });
      // Tier names are embedded in proposal payloads, so those go stale too
      queryClient.invalidateQueries({ queryKey: ["proposal-templates"] });
      queryClient.invalidateQueries({ queryKey: ["proposals"] });
      toast.success("Tier names saved");
    },
    onError: () => toast.error("Failed to save tier names"),
  });

  const resetMutation = useMutation({
    mutationFn: () => proposalsApi.resetTierLabels(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: TIER_LABELS_QUERY_KEY });
      queryClient.invalidateQueries({ queryKey: ["proposal-templates"] });
      queryClient.invalidateQueries({ queryKey: ["proposals"] });
      setDraft(DEFAULT_TIER_LABELS);
      toast.success("Tier names reset to Good / Better / Best");
    },
    onError: () => toast.error("Failed to reset tier names"),
  });

  const saving = saveMutation.isPending || resetMutation.isPending;

  if (isLoading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Typography level="h4" sx={{ mb: 1 }}>
        Name your packages
      </Typography>
      <Card variant="soft" sx={{ mb: 3 }}>
        <CardContent>
          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={2}
            sx={{
              alignItems: { sm: "center" },
              justifyContent: "space-between",
            }}
          >
            <Box>
              <Typography level="body-sm" sx={{ color: "text.secondary" }}>
                Rename the three pricing tiers to match how your company sells.
                These names replace &ldquo;Good&rdquo;, &ldquo;Better&rdquo; and
                &ldquo;Best&rdquo; everywhere &mdash; the proposal builder, the
                proposal your customer sees, and the PDF.
              </Typography>
              {!usingDefaults && (
                <Chip size="sm" variant="soft" color="primary" sx={{ mt: 1 }}>
                  Using custom names
                </Chip>
              )}
            </Box>
            <Stack direction="row" spacing={1} sx={{ flexShrink: 0 }}>
              <Button
                variant="outlined"
                color="neutral"
                startDecorator={<Reset size={18} />}
                disabled={saving || usingDefaults}
                loading={resetMutation.isPending}
                onClick={() => {
                  if (
                    confirm("Reset tier names back to Good, Better and Best?")
                  ) {
                    resetMutation.mutate();
                  }
                }}
              >
                Reset
              </Button>
            </Stack>
          </Stack>
        </CardContent>
      </Card>

      <Card variant="outlined" sx={{ mb: 3 }}>
        <CardContent>
          <Typography
            level="title-md"
            startDecorator={<Tag size={18} />}
            sx={{ mb: 2 }}
          >
            Customer-facing package names{" "}
            <SettingsHelp title="Package names">
              Use short, distinct names such as Essential, Preferred, and
              Premium. This changes the labels, not the products or prices
              inside each package.
            </SettingsHelp>
          </Typography>
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: {
                xs: "1fr",
                md: "repeat(3, minmax(0, 1fr))",
              },
              gap: 3,
            }}
          >
            {TIER_TYPES.map((tierType) => {
              const value = draft[tierType];
              const isBlank = !value.trim();
              const isDuplicate = duplicateTier === tierType;
              return (
                <FormControl key={tierType} error={isBlank || isDuplicate}>
                  <FormLabel>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <Chip
                        size="sm"
                        variant="solid"
                        color={TIER_COLORS[tierType]}
                      >
                        {DEFAULT_TIER_LABELS[tierType]}
                      </Chip>
                      <Typography
                        level="body-sm"
                        sx={{ color: "text.secondary" }}
                      >
                        is called
                      </Typography>
                    </Stack>
                  </FormLabel>
                  <Input
                    value={value}
                    slotProps={{ input: { maxLength: MAX_LENGTH } }}
                    placeholder={DEFAULT_TIER_LABELS[tierType]}
                    onChange={(e) =>
                      setDraft((prev) => ({
                        ...prev,
                        [tierType]: e.target.value,
                      }))
                    }
                  />
                  <FormHelperText>
                    {isBlank
                      ? "Tier name cannot be blank"
                      : isDuplicate
                        ? "Each tier needs a distinct name"
                        : `Shown wherever the ${DEFAULT_TIER_LABELS[tierType].toLowerCase()} tier appears`}
                  </FormHelperText>
                </FormControl>
              );
            })}
          </Box>
        </CardContent>
      </Card>

      <Alert
        variant="soft"
        color="primary"
        startDecorator={<Info size={18} />}
        sx={{ borderRadius: "lg" }}
      >
        <Typography level="body-sm" sx={{ color: "inherit" }}>
          Renaming a tier changes the label only. Existing proposals keep their
          pricing, line items and each tier&rsquo;s own product name &mdash;
          they just display the new label.
        </Typography>
      </Alert>
      <SettingsSaveBar
        dirty={dirty}
        saving={saving}
        label="Save Names"
        disabled={Boolean(blankTier) || Boolean(duplicateTier)}
        onSave={() => saveMutation.mutate()}
      />
    </Box>
  );
}
